/**
 * When each line works, as the work plan shows it: the shifts of the line's own
 * crew (GET ProductionPlan/line-shifts). An order is named by the shift(s) it
 * runs in ("1st", "1st → 2nd") and the part no shift covers is its overtime.
 * Pure — no React, no server.
 */
import { createContext } from "react";

export interface LineShift {
	workstation: string;
	locationId: number;
	shiftId: string;
	/** "1st", "2nd", "12h day" … */
	label: string;
	/** Plant-local ISO, no zone. */
	start: string;
	end: string;
}

export interface OrderShifts {
	/** "1st", "1st → 2nd", or "" when no shift of the line is known. */
	label: string;
	/** Minutes of the order no shift of its line covers (finishing on overtime). */
	offShiftMinutes: number;
}

const ms = (iso: string) => new Date(iso).getTime();

/** The shifts of one line, by workstation name (case-insensitive). */
export function shiftsByLine(shifts: LineShift[]): Map<string, LineShift[]> {
	const map = new Map<string, LineShift[]>();
	shifts.forEach((shift) => {
		const key = shift.workstation.toLowerCase();
		map.set(key, [...(map.get(key) ?? []), shift]);
	});
	map.forEach((list) => list.sort((a, b) => ms(a.start) - ms(b.start)));
	return map;
}

/**
 * The shift(s) an order runs in and how much of it falls outside them. At a
 * handover (2nd starts while 1st still runs) the order belongs to the shift it
 * starts in; a later shift it runs into is named after an arrow.
 */
export function describeOrder(start: string, end: string | undefined | null, lineShifts: LineShift[] | undefined): OrderShifts {
	if (!lineShifts || lineShifts.length === 0) return { label: "", offShiftMinutes: 0 };
	const from = ms(start);
	const to = end ? ms(end) : from;
	const touched = lineShifts.filter((s) => ms(s.start) < Math.max(to, from + 1) && ms(s.end) > from);
	// Starts in: the earliest shift covering the start; else the first it runs into.
	const startsIn = touched.find((s) => ms(s.start) <= from && ms(s.end) > from) ?? touched[0];
	const labels: string[] = [];
	if (startsIn) labels.push(startsIn.label);
	touched
		.filter((s) => startsIn && ms(s.start) >= ms(startsIn.end) - 60 * 60000 && ms(s.start) > ms(startsIn.start))
		.forEach((s) => {
			if (labels[labels.length - 1] !== s.label) labels.push(s.label);
		});

	// Covered minutes: the union of the line's shifts clipped to the order.
	let covered = 0;
	let cursor = from;
	lineShifts.forEach((s) => {
		const a = Math.max(ms(s.start), cursor);
		const b = Math.min(ms(s.end), to);
		if (b > a) {
			covered += b - a;
			cursor = b;
		}
	});
	const offShiftMinutes = Math.max(0, Math.round((to - from - covered) / 60000));
	return { label: labels.join(" → "), offShiftMinutes };
}

/** "1st 07:00–15:30 · 2nd 15:00–23:30": the plant's usual shifts, for the header. */
export function shiftKey(shifts: LineShift[]): string {
	const seen = new Map<string, Map<string, number>>();
	shifts.forEach((s) => {
		const hours = `${s.start.slice(11, 16)}–${s.end.slice(11, 16)}`;
		const counts = seen.get(s.label) ?? new Map<string, number>();
		counts.set(hours, (counts.get(hours) ?? 0) + 1);
		seen.set(s.label, counts);
	});
	return Array.from(seen.entries())
		.map(([label, counts]) => ({ label, hours: Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0][0] }))
		.sort((a, b) => a.hours.localeCompare(b.hours))
		.map(({ label, hours }) => `${label} ${hours}`)
		.join(" · ");
}

/** The plant's line shifts, for the cards that name their shift. */
export const LineShiftsContext = createContext<Map<string, LineShift[]>>(new Map());
